import axios from 'axios';
import api from '../services/api';
import { MSGS } from '../constants/messages.js';

/**
 * Interceptador HTTP centralizado
 * - Adiciona Authorization Bearer (auth_token)
 * - Normaliza erros (kind, userMessage, validationErrors, page, silence)
 * - Registra em axios GLOBAL e na instância api (services/api.js)
 * - Evita duplicidade em HMR usando uma flag em window
 */

function ucfirst(s = '') {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function currentPageSegment() {
  try {
    const path = window.location?.pathname || '';
    const parts = path.split('/').filter(Boolean); // ex.: ["painel", "especialistas"]
    const seg = parts.length >= 2 && parts[0] === 'painel' ? parts[1] : parts[parts.length - 1];
    return ucfirst((seg || '').replace(/-/g, ' '));
  } catch {
    return '';
  }
}

function normalizeError(error) {
  const page = currentPageSegment();
  const resp = error?.response;
  const status = resp?.status;

  error.page = page;
  error.silence = false; // componentes podem usar isso para evitar console.error em erros esperados

  if (!resp) {
    // erro de rede, timeout, CORS, parse etc.
    error.kind = 'exception';
    error.userMessage = `Não foi possível carregar ${page || 'os dados'}. Verifique sua conexão e tente novamente.`;
    return error;
  }

  // 422 validação (devolve payload para forms)
  if (status === 422) {
    error.kind = 'validation';
    error.validationErrors = resp.data?.errors || {};
    error.userMessage = 'Por favor, corrija os erros no formulário.';
    error.silence = true;
    return error;
  }

  if (status === 401) {
    error.kind = 'unauthorized';
    error.userMessage = MSGS.SESSAO_EXPIRADA;
    error.silence = true;
    return error;
  }

  if (status === 403) {
    error.kind = 'permission';
    // mensagem padrão global; componente pode sobrescrever se quiser
    error.userMessage = 'Acesso não permitido.';
    error.silence = true;
    return error;
  }

  if (status === 404) {
    error.kind = 'not_found';
    error.userMessage = `${page || 'Recurso'} não encontrado.`;
    // manter silence=false ajuda a identificar endpoints incorretos em dev
    return error;
  }

  if (status >= 500) {
    error.kind = 'server';
    error.userMessage = 'Erro interno do servidor. Tente novamente.';
    return error;
  }

  // fallback
  error.kind = 'exception';
  error.userMessage = resp.data?.message || `Ocorreu um erro ao processar a solicitação.`;
  return error;
}

/** Registra interceptors em uma instância específica do axios (global ou api). */
function registerInterceptorsFor(instance) {
  // Request: injeta Bearer token
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response: normaliza erros
  instance.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(normalizeError(error))
  );
}

/**
 * Evita múltiplos registros em HMR. Registramos uma única vez por aba.
 * - axios global => __HTTP_INT_AXIOS__
 * - api instance => __HTTP_INT_API__
 */
if (typeof window !== 'undefined') {
  if (!window.__HTTP_INT_AXIOS__) {
    registerInterceptorsFor(axios);
    window.__HTTP_INT_AXIOS__ = true;
  }
  if (!window.__HTTP_INT_API__) {
    registerInterceptorsFor(api);
    window.__HTTP_INT_API__ = true;
  }
} else {
  // SSR/ambiente sem window: registra “na seca”
  registerInterceptorsFor(axios);
  registerInterceptorsFor(api);
}

// Não exportamos nada específico; o objetivo é apenas executar os registros.
export default axios;
